import { useState, useEffect, useRef, useCallback } from 'react';
import DoramaCard from '../components/DoramaCard';
import SkeletonCard from '../components/SkeletonCard';
import CustomSelect from '../components/CustomSelect';
import { useGetMaterialsQuery } from '../services/materialsApi';
import styles from '../css/Catalog.module.css';

const Catalog = () => {
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('Все типы');
  const [selectedGenre, setSelectedGenre] = useState('Все');
  const [selectedYear, setSelectedYear] = useState('Все годы');
  const [selectedCountry, setSelectedCountry] = useState('Все страны');
  const [selectedStatus, setSelectedStatus] = useState('Все');
  const [sortBy, setSortBy] = useState('year');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const dramasRef = useRef([]);
  const lastAppendedPageRef = useRef(0);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const sentinelRef = useRef(null);

  const queryParams = {
    per_page: 24,
    page,
    sort_by: sortBy,
    sort_order: sortOrder,
    ...(search.trim() && { search: search.trim() }),
    ...(selectedType !== 'Все типы' && { type: selectedType }),
    ...(selectedGenre !== 'Все' && { genre: selectedGenre }),
    ...(selectedYear !== 'Все годы' && { year: selectedYear }),
    ...(selectedCountry !== 'Все страны' && { country: selectedCountry }),
    ...(selectedStatus !== 'Все' && { drama_status: selectedStatus }),
  };

  const { data, isLoading, isFetching } = useGetMaterialsQuery(queryParams);

  const totalCount = data?.meta?.total ?? 0;
  const totalPages = data?.meta?.total_pages ?? 1;

  // Накапливаем без useEffect: читаем прямо при рендере, без задержки
  if (data?.data && !isFetching) {
    if (page === 1 && lastAppendedPageRef.current !== 1) {
      dramasRef.current = data.data;
      lastAppendedPageRef.current = 1;
    } else if (page > 1 && lastAppendedPageRef.current < page) {
      dramasRef.current = [...dramasRef.current, ...data.data];
      lastAppendedPageRef.current = page;
    }
  }

  const allDramas = dramasRef.current;

  // IntersectionObserver — загружаем следующую страницу за 200px до конца
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetching && page < totalPages) {
          setPage(p => p + 1);
        }
      },
      { rootMargin: '0px 0px -300px 0px' }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [isFetching, page, totalPages]);

  const typeOptions = [
    { value: 'Все типы', label: 'Все типы' },
    { value: 'foreign-serial', label: 'Сериал' },
    { value: 'foreign-movie', label: 'Фильм' },
  ];

  const genreOptions = [
    { value: 'Все', label: 'Все' },
    { value: 'Романтика', label: 'Романтика' },
    { value: 'Драма', label: 'Драма' },
    { value: 'Мелодрама', label: 'Мелодрама' },
    { value: 'Комедия', label: 'Комедия' },
    { value: 'Фэнтези', label: 'Фэнтези' },
    { value: 'Исторический', label: 'Исторический' },
    { value: 'Триллер', label: 'Триллер' },
    { value: 'Детектив', label: 'Детектив' },
    { value: 'Боевик', label: 'Боевик' },
    { value: 'Экшен', label: 'Экшен' },
    { value: 'Школа', label: 'Школа' },
    { value: 'Криминал', label: 'Криминал' },
    { value: 'Мистика', label: 'Мистика' },
    { value: 'Ужасы', label: 'Ужасы' },
    { value: 'Фантастика', label: 'Фантастика' },
    { value: 'Психологическое', label: 'Психологическое' },
    { value: 'Медицина', label: 'Медицина' },
    { value: 'Спорт', label: 'Спорт' },
    { value: 'Военный', label: 'Военный' },
    { value: 'Приключения', label: 'Приключения' },
    { value: 'Сверхъестественное', label: 'Сверхъестественное' },
    { value: 'Семейный', label: 'Семейный' },
    { value: 'Музыка', label: 'Музыка' },
  ];

  const yearOptions = ['Все годы', '2026', '2025', '2024', '2023', '2022', '2021', '2020', '2019', '2018', '2017', '2016', '2015', '2013', '2010', '2009']
    .map((y) => ({ value: y, label: y }));

  const countryOptions = ['Все страны', 'Корея Южная', 'Япония', 'Китай', 'Таиланд']
    .map((c) => ({ value: c, label: c }));

  const statusOptions = [
    { value: 'Все', label: 'Все' },
    { value: 'ongoing', label: 'Онгоинг' },
    { value: 'released', label: 'Выпущен' },
    { value: 'anons', label: 'Анонс' },
  ];

  const sortByOptions = [
    { value: 'year', label: 'По году' },
    { value: 'title', label: 'По названию' },
    { value: 'kinopoisk_rating', label: 'По рейтингу КП' },
    { value: 'imdb_rating', label: 'По рейтингу IMDB' },
    { value: 'mydramalist_rating', label: 'По рейтингу MDL' },
  ];

  const sortOrderOptions = [
    { value: 'desc', label: 'По убыванию' },
    { value: 'asc', label: 'По возрастанию' },
  ];

  // Блокируем скролл и прячем навбар когда открыт оверлей
  useEffect(() => {
    const isOpen = mobileFiltersOpen || mobileSortOpen;
    if (isOpen) {
      document.body.classList.add('overlay-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('overlay-open');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('overlay-open');
      document.body.style.overflow = '';
    };
  }, [mobileFiltersOpen, mobileSortOpen]);

  const resetPage = useCallback(() => {
    dramasRef.current = [];
    lastAppendedPageRef.current = 0;
    setPage(1);
  }, []);

  const handleSelectChange = (setter) => (val) => {
    setter(val);
    resetPage();
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    resetPage();
  };

  const activeFiltersCount = [
    selectedType !== 'Все типы',
    selectedGenre !== 'Все',
    selectedYear !== 'Все годы',
    selectedCountry !== 'Все страны',
    selectedStatus !== 'Все',
    search.trim() !== '',
  ].filter(Boolean).length;

  return (
    <div className={styles.catalogPage}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Каталог</h1>
          <p className={styles.subtitle}>Все дорамы в одном месте</p>
        </div>

        {/* Search bar */}
        <div className={styles.searchBar}>
          <svg className={styles.searchIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Поиск по названию..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>

        {/* Desktop filters */}
        <div className={styles.filtersSection}>
          <CustomSelect label="Тип" value={selectedType} options={typeOptions} onChange={handleSelectChange(setSelectedType)} />
          <CustomSelect label="Жанр" value={selectedGenre} options={genreOptions} onChange={handleSelectChange(setSelectedGenre)} />
          <CustomSelect label="Год" value={selectedYear} options={yearOptions} onChange={handleSelectChange(setSelectedYear)} />
          <CustomSelect label="Страна" value={selectedCountry} options={countryOptions} onChange={handleSelectChange(setSelectedCountry)} />
          <CustomSelect label="Статус" value={selectedStatus} options={statusOptions} onChange={handleSelectChange(setSelectedStatus)} />
          <CustomSelect label="Сортировка" value={sortBy} options={sortByOptions} onChange={handleSelectChange(setSortBy)} />
          <CustomSelect label="Порядок" value={sortOrder} options={sortOrderOptions} onChange={handleSelectChange(setSortOrder)} />
        </div>

        {/* Mobile toolbar */}
        <div className={styles.mobileToolbar}>
          <button className={styles.mobileActionBtn} onClick={() => setMobileFiltersOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M7 12h10M10 18h4"/>
            </svg>
            Фильтры
            {activeFiltersCount > 0 && <span className={styles.filtersBadge}>{activeFiltersCount}</span>}
          </button>
          <button className={styles.mobileActionBtn} onClick={() => setMobileSortOpen(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7"/><path d="M3 15l9 7 9-7"/>
            </svg>
            Сортировка
          </button>
        </div>

        {/* Mobile filters overlay */}
        {mobileFiltersOpen && (
          <div className={styles.overlay} onClick={() => setMobileFiltersOpen(false)}>
            <div className={styles.overlayPanel} onClick={e => e.stopPropagation()}>
              <div className={styles.overlayHeader}>
                <span>Фильтры</span>
                <button className={styles.overlayClose} onClick={() => setMobileFiltersOpen(false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <div className={styles.overlayBody}>
                <CustomSelect label="Тип" value={selectedType} options={typeOptions} onChange={handleSelectChange(setSelectedType)} />
                <CustomSelect label="Жанр" value={selectedGenre} options={genreOptions} onChange={handleSelectChange(setSelectedGenre)} />
                <CustomSelect label="Год" value={selectedYear} options={yearOptions} onChange={handleSelectChange(setSelectedYear)} />
                <CustomSelect label="Страна" value={selectedCountry} options={countryOptions} onChange={handleSelectChange(setSelectedCountry)} />
                <CustomSelect label="Статус" value={selectedStatus} options={statusOptions} onChange={handleSelectChange(setSelectedStatus)} />
              </div>
              <div className={styles.overlayFooter}>
                <button className={styles.overlayApply} onClick={() => setMobileFiltersOpen(false)}>Применить</button>
                <button className={styles.overlayReset} onClick={() => {
                  setSelectedType('Все типы');
                  setSelectedGenre('Все');
                  setSelectedYear('Все годы');
                  setSelectedCountry('Все страны');
                  setSelectedStatus('Все');
                  resetPage();
                }}>Сбросить</button>
              </div>
            </div>
          </div>
        )}

        {/* Mobile sort bottom sheet */}
        {mobileSortOpen && (
          <div className={styles.overlay} onClick={() => setMobileSortOpen(false)}>
            <div className={styles.sortSheet} onClick={e => e.stopPropagation()}>
              <div className={styles.sortSheetHandle} />
              <div className={styles.overlayHeader}>
                <span>Сортировка</span>
                <button className={styles.overlayClose} onClick={() => setMobileSortOpen(false)}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              <div className={styles.sortSheetBody}>
                <p className={styles.sortGroupLabel}>Сортировать по</p>
                {sortByOptions.map(opt => (
                  <button
                    key={opt.value}
                    className={`${styles.sortOption} ${sortBy === opt.value ? styles.sortOptionActive : ''}`}
                    onClick={() => { setSortBy(opt.value); resetPage(); }}
                  >
                    {opt.label}
                    {sortBy === opt.value && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    )}
                  </button>
                ))}
                <p className={styles.sortGroupLabel}>Порядок</p>
                {sortOrderOptions.map(opt => (
                  <button
                    key={opt.value}
                    className={`${styles.sortOption} ${sortOrder === opt.value ? styles.sortOptionActive : ''}`}
                    onClick={() => { setSortOrder(opt.value); resetPage(); }}
                  >
                    {opt.label}
                    {sortOrder === opt.value && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
              <div className={styles.overlayFooter}>
                <button className={styles.overlayApply} onClick={() => setMobileSortOpen(false)}>Готово</button>
              </div>
            </div>
          </div>
        )}

        <div className={styles.resultsInfo}>
          <p>Найдено: <span className={styles.count}>{totalCount}</span> дорам</p>
        </div>

        {(isLoading || isFetching) && allDramas.length === 0 ? (
          <div className={styles.dramasGrid}>
            {Array.from({ length: 24 }, (_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : (
          <div className={styles.dramasGrid}>
            {allDramas.map(drama => (
              <DoramaCard
                key={drama.id}
                id={drama.id}
                slug={drama.slug}
                slug_id={drama.slug_id}
                image={`https://doramahype.ru/uploads/posters/${drama.poster_url}`}
                title={drama.title}
                rating={drama.imdb_rating}
                mdl_rating={drama.mydramalist_rating}
                kinopoisk_rating={drama.kinopoisk_rating}
                year={drama.year}
                genres={drama.genres ?? []}
                status={drama.drama_status}
              />
            ))}
          </div>
        )}

        {!isLoading && allDramas.length === 0 && (
          <div className={styles.noResults}>
            <p>По вашим фильтрам ничего не найдено</p>
            <p className={styles.noResultsSubtext}>Попробуйте изменить параметры поиска</p>
          </div>
        )}

        <div ref={sentinelRef} className={styles.sentinel} />

        {isFetching && page > 1 && (
          <div className={styles.loadingMore}>
            <div className={styles.spinner} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalog;
